import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { useResponsive } from '../../../hooks/ResponsiveProvider';

const ImagePreview = ({
  src,
  alt = 'Uploaded image',
  loading = false,
  error = false,
  unavailable = false,
  fileName,
}) => {
  const responsive = useResponsive() || {};
  const { isSmallScreen, isMediumScreen } = responsive;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const maxHeight = isSmallScreen ? 180 : isMediumScreen ? 240 : 320;
  const showError = error || imageError;
  const canOpenLightbox = Boolean(src) && !loading && !showError && !unavailable;

  useEffect(() => {
    setImageError(false);
    setLightboxOpen(false);
  }, [src]);

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading preview...
          </Typography>
        </Box>
      );
    }

    if (unavailable) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          Preview not available
        </Typography>
      );
    }

    if (showError) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <BrokenImageOutlinedIcon color="disabled" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Failed to load image preview
          </Typography>
        </Box>
      );
    }

    if (!src) {
      return null;
    }

    return (
      <Box
        component="img"
        src={src}
        alt={alt || fileName || 'Uploaded image'}
        loading="lazy"
        onClick={() => canOpenLightbox && setLightboxOpen(true)}
        onError={() => setImageError(true)}
        sx={{
          display: 'block',
          width: 'auto',
          maxWidth: '100%',
          height: 'auto',
          maxHeight,
          objectFit: 'contain',
          cursor: canOpenLightbox ? 'zoom-in' : 'default',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        mt: src || loading || showError || unavailable ? 1 : 0,
      }}
    >
      {renderBody()}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            m: 1,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'common.black',
          },
        }}
      >
        <IconButton
          aria-label="Close image preview"
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: 'common.white',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '90vw',
            maxHeight: '90vh',
            p: 1,
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt || fileName || 'Uploaded image'}
            sx={{
              display: 'block',
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default React.memo(ImagePreview);
